/**
 * Coûts d'une session repris du référentiel Lieux.
 *
 * Deux lignes de la rentabilité (onglet Facturation de la fiche session) peuvent
 * venir de la fiche d'un lieu :
 *   « Location salle » -> sessions.frais_salle            <- lieux_formation.prix_*
 *   « Restauration »   -> sessions.restauration_apprenant <- lieu_prestations
 *
 * Même principe que le tarif formateur (cf. trainerRate.js) : le montant est
 * copié sur la session au moment de la reprise, jamais relu en direct. Une
 * renégociation du tarif d'un lieu ne doit pas modifier après coup la marge des
 * sessions déjà facturées.
 */

/**
 * Types de `lieu_prestations` qui relèvent de la restauration.
 * Valeurs alignées sur PRESTATION_TYPE_OPTIONS (lieuxConstants.js) : les autres
 * types (location_materiel, salle_supplementaire, autre) ne sont pas des repas
 * et n'ont pas leur place sur la ligne « Restauration ».
 */
export const RESTAURATION_TYPES = ['petit_dejeuner', 'dejeuner', 'collation', 'pause_cafe'];

/** Unité des prestations refacturées à l'apprenant. */
const UNITE_PAR_PERSONNE = 'par personne';

/** Convertit en nombre exploitable ; null si la valeur est absente ou vide. */
const toAmount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Un coût porté par la session est-il encore « libre » ?
 *
 * `frais_salle` et `restauration_apprenant` sont déclarées NOT NULL DEFAULT 0.00
 * (add_profitability_columns.php, dont le commentaire pose la règle : « la charge
 * d'une session reste nulle tant qu'aucun coût réel n'a été saisi »). 0 signifie
 * donc « non renseigné » et peut être repris ; toute autre valeur a été arbitrée
 * par quelqu'un et n'est jamais écrasée.
 */
export const isCostUnset = (value) => {
  const n = toAmount(value);
  return n === null || n === 0;
};

/**
 * Une session de ce format se déroule-t-elle sans salle à louer ?
 *
 * Le critère est le format de la FORMATION, pas la présence d'un `meeting_link`
 * sur la session : ce lien est un mauvais signal dans les deux sens — 85 sessions
 * « Classe virtuelle » n'en portent aucun, et des sessions « Présentiel » en
 * traînent un sans être virtuelles pour autant.
 *
 * Valeurs reconnues : cf. ALL_FORMAT_VALUES (formationFormats.js). « Mixte » et
 * « E-learning/Présentiel » comportent du présentiel, donc une salle. Un format
 * inconnu est traité comme présentiel : sans lieu rattaché la question ne se pose
 * pas de toute façon, et mieux vaut compter une salle réelle que l'oublier.
 */
export const isRoomlessFormat = (format) => {
  const f = (format || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // é -> e, à -> a…
    .toLowerCase().trim();
  if (f === '') return false;
  if (f.includes('non presentiel')) return true;   // avant le test « presentiel »
  if (f.includes('presentiel')) return false;      // Présentiel, Présentiel PI, E-learning/Présentiel
  return f.includes('classe virtuelle') || f.includes('learning') || f === 'epp';
};

/** Nombre de jours facturés pour la session (même compteur que le formateur). */
const sessionDays = (session) => {
  const d = toAmount(session?.trainer_days);
  return d !== null && d > 0 ? d : 1;
};

/**
 * Coût de location de la salle pour une session.
 *
 * Renvoie `{ amount, formula }` ou null si le lieu ne permet aucun calcul.
 * `formula` est affichée telle quelle à l'utilisateur : toute règle de conversion
 * doit rester lisible plutôt que silencieuse.
 *
 * Une session dont le format ne comporte pas de présentiel n'a pas de salle à
 * payer : on ne calcule rien. Le critère est le format de la formation, jamais
 * le `meeting_link` de la session (cf. isRoomlessFormat).
 */
export const resolveRoomCost = (lieu, session, format) => {
  if (!lieu || isRoomlessFormat(format)) return null;
  const days = sessionDays(session);
  const jour = toAmount(lieu.prix_journee);
  if (jour !== null && jour > 0) {
    return { amount: jour * days, formula: `${jour} €/jour × ${days} j` };
  }
  // Pas de prix journée : on reconstitue la journée à partir de la demi-journée.
  // Deux demi-journées = une journée, découpage déjà retenu pour les vacations
  // des feuilles d'émargement (EmargementSheet::MORNING_HOURS / AFTERNOON_HOURS).
  const demi = toAmount(lieu.prix_demi_jour);
  if (demi !== null && demi > 0) {
    return { amount: demi * 2 * days, formula: `${demi} €/demi-journée × 2 × ${days} j` };
  }
  // `prix_heure` seul ne suffit pas : la fiche session ne porte pas de volume
  // horaire exploitable, on préfère ne rien reprendre plutôt que d'inventer.
  return null;
};

/**
 * Coût de restauration par apprenant, repris du catalogue de prestations du lieu.
 *
 * La rentabilité multiplie `restauration_apprenant` par le nombre d'inscrits :
 * seules les prestations facturées « par personne » sont donc additionnées. Les
 * unités « par jour », « forfait » et « unitaire » sont des coûts de session, pas
 * des coûts par tête — elles relèvent des autres charges, saisies à la main.
 *
 * Même règle de format que la salle : personne ne déjeune sur place en classe
 * virtuelle, en EPP ou en e-learning, même si un lieu reste rattaché à la
 * session. Le format de la formation décide, jamais le `meeting_link`.
 *
 * Renvoie `{ amount, formula }` ou null si le catalogue ne contient aucun repas.
 */
export const resolveRestaurationPerLearner = (prestations, format) => {
  if (isRoomlessFormat(format)) return null;
  if (!Array.isArray(prestations) || prestations.length === 0) return null;
  const repas = prestations.filter((p) => (
    RESTAURATION_TYPES.includes(p.type) && p.unite === UNITE_PAR_PERSONNE
  ));
  if (repas.length === 0) return null;
  const amount = repas.reduce((sum, p) => sum + (toAmount(p.prix_unitaire) || 0), 0);
  if (amount <= 0) return null;
  return { amount, formula: repas.map((p) => p.libelle).filter(Boolean).join(' + ') };
};
